import { useState } from "react";
import { useCustom, useList } from "@refinedev/core";
import {
  Activity,
  Monitor,
  Smartphone,
  Tablet,
  Globe,
  Clock,
  MousePointerClick,
  Eye,
  Loader2,
  Wifi,
  WifiOff,
  BookOpen,
  Play,
  CheckCircle,
  Search,
  ShoppingCart,
  Heart,
  MessageSquare,
  Share2,
  CreditCard,
  UserIcon,
  Bell,
  Gamepad2,
  Target,
  CalendarDays,
  LogOut,
  ScrollText,
  Tag,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { DateRangePicker } from "@/components/ui/date-range-picker";
import type { DateRange } from "react-day-picker";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

interface ActionPayload {
  entityType?: string;
  entityId?: number;
  slug?: string;
  lessonId?: number;
  courseId?: number;
  postId?: number;
  query?: string;
  resultsCount?: number;
  position?: number;
  timeOnPage?: number;
  depth?: number;
  gameType?: string;
  score?: number;
  won?: boolean;
  viewedUserId?: number;
  orderId?: number;
  ts?: number;
  [key: string]: string | number | boolean | undefined;
}

interface UserAction {
  id: number;
  type: string;
  page: string | null;
  component: string | null;
  payload: ActionPayload | null;
  createdAt: string;
}

interface UserAnalytics {
  totalSessions: number;
  totalActions: number;
  totalTimeSpent: number;
  averageSessionDuration: number;
  pageViews: number;
  uniquePages: number;
  lastSeenAt: string | null;
  isOnline: boolean;
  deviceBreakdown: Record<string, number>;
  browserBreakdown: Record<string, number>;
  topPages: { page: string; views: number }[];
}

interface UserActivityLogProps {
  userId: number;
}

const PAGE_LABELS: Record<string, string> = {
  dashboard: "Главная",
  "my-courses": "Моё обучение",
  courses: "Курсы",
  club: "Masters Club",
  content: "Контент",
  articles: "Статьи",
  teachers: "Преподаватели",
  planner: "Ежедневник",
  games: "Игры",
  "card-color": "Угадай цвет",
  "guess-number": "Угадай число",
  lobby: "Лобби",
  play: "Игра",
  chat: "Чат",
  subscriptions: "Подписки",
  "channel-passes": "Каналы",
  bonuses: "Бонусы",
  settings: "Настройки",
  privacy: "Конфиденциальность",
  profile: "Профиль",
  notifications: "Уведомления",
  checkout: "Оплата",
  success: "Успешно",
  lessons: "Уроки",
  posts: "Посты",
  meetings: "Встречи",
  members: "Участники",
  pricing: "Тарифы",
  family: "Семейная подписка",
};

const LANG_CODES = new Set(["ru", "en", "uz", "tr", "kz"]);

function formatPagePath(rawPage: string): string {
  const segments = rawPage.split("/").filter(Boolean);
  // Убираем языковой префикс
  const filtered = segments.filter((s) => !LANG_CODES.has(s));
  if (filtered.length === 0) {return "Главная";}

  const parts: string[] = [];
  for (let i = 0; i < filtered.length; i++) {
    const seg = filtered[i] ?? '';
    const prev = i > 0 ? filtered[i - 1] : undefined;

    // Пропускаем "lessons" как промежуточный сегмент
    if (seg === "lessons" && i + 1 < filtered.length) {continue;}

    // Числовые ID после контекста
    if (!isNaN(Number(seg))) {
      if (prev === "lessons" || (i >= 2 && filtered[i - 2] === "lessons")) {
        parts.push(`Урок #${seg}`);
      } else if (prev === "teachers") {
        parts.push(`#${seg}`);
      } else if (prev === "chat") {
        parts.push(`#${seg}`);
      } else if (prev === "lobby" || prev === "play") {
        parts.push(`#${seg}`);
      } else {
        parts.push(`#${seg}`);
      }
      continue;
    }

    // Slug курса после courses/my-courses
    if (prev === "courses" || prev === "my-courses") {
      parts.push(seg.replaceAll("-", " "));
      continue;
    }

    // Статические лейблы
    const label = PAGE_LABELS[seg];
    if (label) {
      parts.push(label);
    } else {
      parts.push(seg.replaceAll("-", " "));
    }
  }

  return parts.join(" / ");
}
export function UserActivityLog({ userId }: UserActivityLogProps) {
  const [page, setPage] = useState(1);
  const [actionType, setActionType] = useState<string>("all");
  const [dateRange, setDateRange] = useState<DateRange | null>(null);
  const pageSize = 15;

  // Fetch analytics
  const { result: analyticsResult, query: analyticsQueryState } =
    useCustom<UserAnalytics>({
      url: `/telemetry/users/${userId}/analytics`,
      method: "get",
    });

  // Fetch actions
  const { result, query } = useList<UserAction>({
    resource: "telemetry/actions",
    pagination: { currentPage: page, pageSize, mode: "server" },
    filters: [
      { field: "userId", operator: "eq", value: userId },
      ...(actionType !== "all"
        ? [{ field: "type", operator: "eq" as const, value: actionType }]
        : []),
      ...(dateRange?.from
        ? [{ field: "fromDate", operator: "eq" as const, value: dateRange.from.toISOString() }]
        : []),
      ...(dateRange?.to
        ? [{ field: "toDate", operator: "eq" as const, value: dateRange.to.toISOString() }]
        : []),
    ],
    sorters: [{ field: "createdAt", order: "desc" }],
  });

  const actions = result.data ?? [];
  const total = result.total ?? 0;
  const isLoading = query.isLoading;
  const analyticsLoading = analyticsQueryState.isLoading;
  const analytics = analyticsResult?.data;

  const formatDuration = (seconds: number) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    if (hours > 0) {return `${hours}ч ${minutes}м`;}
    return `${minutes}м`;
  };

  const formatTime = (dateString: string) => {
    return new Date(dateString).toLocaleString("ru-RU", {
      day: "numeric",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getActionIcon = (type: string) => {
    switch (type) {
      case "page_view":
      case "page_exit":
        return <Eye className="h-4 w-4" />;
      case "scroll_depth":
        return <ScrollText className="h-4 w-4" />;
      case "course_view":
      case "course_view_long":
      case "course_curriculum_expanded":
        return <BookOpen className="h-4 w-4" />;
      case "course_pricing_viewed":
        return <Tag className="h-4 w-4" />;
      case "lesson_started":
      case "lesson_video_play":
        return <Play className="h-4 w-4" />;
      case "lesson_completed":
      case "course_completed":
      case "homework_submitted":
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case "homework_started":
        return <BookOpen className="h-4 w-4" />;
      case "search_query":
      case "search_result_click":
      case "search_no_results":
        return <Search className="h-4 w-4" />;
      case "checkout_started":
      case "checkout_completed":
      case "checkout_abandoned":
        return <ShoppingCart className="h-4 w-4" />;
      case "subscription_started":
      case "subscription_renewed":
        return <CreditCard className="h-4 w-4 text-green-500" />;
      case "subscription_cancelled":
        return <CreditCard className="h-4 w-4 text-red-500" />;
      case "post_like":
        return <Heart className="h-4 w-4" />;
      case "post_comment":
        return <MessageSquare className="h-4 w-4" />;
      case "post_share":
      case "course_shared":
        return <Share2 className="h-4 w-4" />;
      case "post_view":
      case "article_view":
      case "article_read_complete":
        return <Eye className="h-4 w-4" />;
      case "profile_view":
      case "teacher_view":
      case "user_follow":
      case "user_unfollow":
        return <UserIcon className="h-4 w-4" />;
      case "notification_clicked":
        return <Bell className="h-4 w-4" />;
      case "game_started":
      case "game_completed":
        return <Gamepad2 className="h-4 w-4" />;
      case "goal_created":
        return <Target className="h-4 w-4" />;
      case "daily_planner_opened":
        return <CalendarDays className="h-4 w-4" />;
      case "click":
        return <MousePointerClick className="h-4 w-4" />;
      case "session_end":
        return <LogOut className="h-4 w-4" />;
      default:
        return <Activity className="h-4 w-4" />;
    }
  };

  const ACTION_LABELS: Record<string, string> = {
    page_view: "Просмотр страницы",
    page_exit: "Уход со страницы",
    scroll_depth: "Глубина скролла",
    tab_visible: "Вкладка активна",
    tab_hidden: "Вкладка скрыта",
    course_view: "Просмотр курса",
    course_view_long: "Долгий просмотр курса",
    course_curriculum_expanded: "Раскрыл программу",
    course_pricing_viewed: "Смотрел цены",
    lesson_started: "Начал урок",
    lesson_completed: "Завершил урок",
    lesson_video_play: "Запустил видео",
    lesson_video_progress: "Прогресс видео",
    homework_started: "Начал ДЗ",
    homework_submitted: "Сдал ДЗ",
    course_completed: "Завершил курс",
    course_review_written: "Написал отзыв",
    course_shared: "Поделился курсом",
    article_view: "Просмотр статьи",
    article_read_complete: "Дочитал статью",
    post_view: "Просмотр поста",
    post_like: "Лайк поста",
    post_comment: "Комментарий",
    post_share: "Поделился постом",
    search_query: "Поиск",
    search_result_click: "Клик из поиска",
    search_no_results: "Пустой поиск",
    search_filter_applied: "Фильтр поиска",
    checkout_started: "Начал оформление",
    checkout_completed: "Оплатил",
    checkout_abandoned: "Бросил корзину",
    subscription_started: "Оформил подписку",
    subscription_cancelled: "Отменил подписку",
    subscription_renewed: "Продлил подписку",
    profile_view: "Просмотр профиля",
    teacher_view: "Просмотр учителя",
    user_follow: "Подписался",
    user_unfollow: "Отписался",
    chat_message_sent: "Сообщение в чат",
    chat_room_entered: "Вошёл в чат",
    notification_clicked: "Клик уведомления",
    daily_planner_opened: "Открыл планнер",
    goal_created: "Создал цель",
    game_started: "Начал игру",
    game_completed: "Завершил игру",
    achievement_unlocked: "Достижение",
    bonus_earned: "Бонус",
    session_start: "Начало сессии",
    session_end: "Конец сессии",
    click: "Клик",
    scroll: "Скролл",
    focus: "Фокус",
    blur: "Потеря фокуса",
    form_submit: "Отправка формы",
  };

  const getActionLabel = (type: string) => ACTION_LABELS[type] ?? type;

  const getActionCategory = (type: string): string => {
    if (["page_view", "page_exit", "scroll_depth", "tab_visible", "tab_hidden"].includes(type)) {return "navigation";}
    if (type.startsWith("course_") || type.startsWith("lesson_") || type.startsWith("homework_")) {return "course";}
    if (["article_view", "article_read_complete", "post_view", "post_like", "post_comment", "post_share"].includes(type)) {return "content";}
    if (type.startsWith("search_")) {return "search";}
    if (type.startsWith("checkout_") || type.startsWith("subscription_")) {return "commerce";}
    if (["profile_view", "teacher_view", "user_follow", "user_unfollow", "chat_message_sent", "chat_room_entered"].includes(type)) {return "social";}
    if (["game_started", "game_completed", "daily_planner_opened", "goal_created", "notification_clicked", "achievement_unlocked", "bonus_earned"].includes(type)) {return "engagement";}
    return "other";
  };

  const getCategoryColor = (type: string): string => {
    switch (getActionCategory(type)) {
      case "course": return "bg-blue-500/10 text-blue-600";
      case "commerce": return "bg-green-500/10 text-green-600";
      case "content": return "bg-purple-500/10 text-purple-600";
      case "search": return "bg-orange-500/10 text-orange-600";
      case "social": return "bg-pink-500/10 text-pink-600";
      case "engagement": return "bg-yellow-500/10 text-yellow-600";
      case "navigation": return "bg-gray-500/10 text-gray-600";
      default: return "bg-muted text-muted-foreground";
    }
  };

  const formatPayloadDetails = (action: UserAction): string => {
    const p = action.payload;
    if (!p) {return "—";}
    const parts: string[] = [];

    if (p.entityType && p.entityId) {
      parts.push(`${p.entityType} #${p.entityId}`);
    }
    if (p.slug) {parts.push(p.slug);}
    if (p.lessonId) {parts.push(`урок #${p.lessonId}`);}
    if (p.query) {parts.push(`«${p.query}»`);}
    if (p.resultsCount !== undefined) {parts.push(`${p.resultsCount} рез.`);}
    if (p.position !== undefined) {parts.push(`поз. ${p.position}`);}
    if (p.timeOnPage) {parts.push(`${p.timeOnPage}с`);}
    if (p.depth) {parts.push(`${p.depth}%`);}
    if (p.score !== undefined) {parts.push(`очки: ${p.score}`);}
    if (p.won !== undefined) {parts.push(p.won ? "победа" : "поражение");}
    if (p.gameType) {parts.push(p.gameType);}
    if (p.orderId) {parts.push(`заказ #${p.orderId}`);}

    return parts.length > 0 ? parts.join(" · ") : "—";
  };

  const getDeviceIcon = (device: string) => {
    switch (device.toLowerCase()) {
      case "mobile":
        return <Smartphone className="h-4 w-4" />;
      case "tablet":
        return <Tablet className="h-4 w-4" />;
      default:
        return <Monitor className="h-4 w-4" />;
    }
  };

  return (
    <div className="space-y-6">
      {/* Stats Row */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Статус</CardTitle>
            {analytics?.isOnline ? (
              <Wifi className="h-4 w-4 text-green-500" />
            ) : (
              <WifiOff className="h-4 w-4 text-muted-foreground" />
            )}
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2">
              <Badge
                className={
                  analytics?.isOnline
                    ? "bg-green-500/10 text-green-500"
                    : "bg-muted text-muted-foreground"
                }
              >
                {analytics?.isOnline ? "Online" : "Offline"}
              </Badge>
            </div>
            {analytics?.lastSeenAt && (
              <p className="text-xs text-muted-foreground mt-1">
                Последняя активность: {formatTime(analytics.lastSeenAt)}
              </p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Сессий</CardTitle>
            <Activity className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {analyticsLoading ? "..." : analytics?.totalSessions ?? 0}
            </div>
            <p className="text-xs text-muted-foreground">
              {analytics?.totalActions ?? 0} действий
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Время в приложении
            </CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {analyticsLoading
                ? "..."
                : formatDuration(analytics?.totalTimeSpent ?? 0)}
            </div>
            <p className="text-xs text-muted-foreground">
              Средняя сессия:{" "}
              {formatDuration(analytics?.averageSessionDuration ?? 0)}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Страниц</CardTitle>
            <Globe className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {analyticsLoading ? "..." : analytics?.pageViews ?? 0}
            </div>
            <p className="text-xs text-muted-foreground">
              {analytics?.uniquePages ?? 0} уникальных
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Device & Browser breakdown */}
      {analytics && (
        <div className="grid gap-4 md:grid-cols-2">
          <Card>
            <CardHeader>
              <CardTitle className="text-sm">Устройства</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex gap-4">
                {Object.entries(analytics.deviceBreakdown || {}).map(
                  ([device, count]) => (
                    <div key={device} className="flex items-center gap-2">
                      {getDeviceIcon(device)}
                      <span className="capitalize">{device}</span>
                      <Badge variant="secondary">{count}</Badge>
                    </div>
                  )
                )}
                {Object.keys(analytics.deviceBreakdown || {}).length === 0 && (
                  <span className="text-muted-foreground">Нет данных</span>
                )}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-sm">Топ страниц</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {(analytics.topPages || []).slice(0, 5).map((p, i) => (
                  <div key={i} className="flex justify-between text-sm">
                    <span className="truncate" title={p.page}>{formatPagePath(p.page)}</span>
                    <Badge variant="outline">{p.views}</Badge>
                  </div>
                ))}
                {(analytics.topPages || []).length === 0 && (
                  <span className="text-muted-foreground">Нет данных</span>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Actions Table */}
      <Card>
        <CardHeader className="space-y-3">
          <div className="flex items-center justify-between">
            <CardTitle>Последние действия</CardTitle>
            <Badge variant="outline">{total} событий</Badge>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <Select value={actionType} onValueChange={(v) => { setActionType(v); setPage(1); }}>
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder="Тип события" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Все события</SelectItem>
                <SelectItem value="page_view">Просмотры страниц</SelectItem>
                <SelectItem value="page_exit">Уход со страницы</SelectItem>
                <SelectItem value="course_view">Просмотры курсов</SelectItem>
                <SelectItem value="course_view_long">Долгие просмотры курсов</SelectItem>
                <SelectItem value="course_pricing_viewed">Просмотр цен</SelectItem>
                <SelectItem value="course_curriculum_expanded">Раскрытие программы</SelectItem>
                <SelectItem value="lesson_started">Начало уроков</SelectItem>
                <SelectItem value="lesson_completed">Завершение уроков</SelectItem>
                <SelectItem value="lesson_video_play">Запуск видео</SelectItem>
                <SelectItem value="course_completed">Завершение курса</SelectItem>
                <SelectItem value="checkout_started">Оформление заказов</SelectItem>
                <SelectItem value="checkout_completed">Оплаты</SelectItem>
                <SelectItem value="checkout_abandoned">Брошенные корзины</SelectItem>
                <SelectItem value="search_query">Поисковые запросы</SelectItem>
                <SelectItem value="search_result_click">Клики из поиска</SelectItem>
                <SelectItem value="post_view">Просмотры постов</SelectItem>
                <SelectItem value="post_like">Лайки постов</SelectItem>
                <SelectItem value="post_comment">Комментарии</SelectItem>
                <SelectItem value="teacher_view">Просмотры учителей</SelectItem>
                <SelectItem value="profile_view">Просмотры профилей</SelectItem>
                <SelectItem value="game_started">Начало игр</SelectItem>
                <SelectItem value="game_completed">Завершение игр</SelectItem>
                <SelectItem value="daily_planner_opened">Планировщик</SelectItem>
                <SelectItem value="goal_created">Создание целей</SelectItem>
                <SelectItem value="subscription_started">Оформление подписок</SelectItem>
                <SelectItem value="subscription_cancelled">Отмена подписок</SelectItem>
              </SelectContent>
            </Select>
            <DateRangePicker
              value={dateRange}
              onChange={(range) => { setDateRange(range); setPage(1); }}
              placeholder="Период"
            />
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin" />
            </div>
          ) : (actions.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">
              Действий пока нет
            </p>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[140px]">Время</TableHead>
                    <TableHead className="w-[200px]">Событие</TableHead>
                    <TableHead>Страница</TableHead>
                    <TableHead>Детали</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {actions.map((action) => (
                    <TableRow key={action.id}>
                      <TableCell className="whitespace-nowrap text-xs text-muted-foreground">
                        {formatTime(action.createdAt)}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {getActionIcon(action.type)}
                          <Badge variant="outline" className={getCategoryColor(action.type)}>
                            {getActionLabel(action.type)}
                          </Badge>
                        </div>
                      </TableCell>
                      <TableCell className="max-w-[250px]">
                        <div className="flex flex-col">
                          <span className="block truncate text-sm" title={action.page ?? undefined}>
                            {action.page ? formatPagePath(action.page) : "—"}
                          </span>
                          {action.page && (
                            <span className="block truncate text-xs text-muted-foreground" title={action.page}>
                              {action.page}
                            </span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="max-w-[250px]">
                        <span className="block truncate text-sm text-muted-foreground" title={formatPayloadDetails(action)}>
                          {formatPayloadDetails(action)}
                        </span>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {total > pageSize && (
                <div className="flex justify-between items-center mt-4">
                  <p className="text-sm text-muted-foreground">
                    Страница {page} из {Math.ceil(total / pageSize)}
                  </p>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      disabled={page === 1}
                    >
                      Назад
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage((p) => p + 1)}
                      disabled={page >= Math.ceil(total / pageSize)}
                    >
                      Вперёд
                    </Button>
                  </div>
                </div>
              )}
            </>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}
